import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Paper, Switch, FormControlLabel, Button, Alert, Typography, Chip, TextField, MenuItem,
} from '@mui/material';
import ExtensionIcon from '@mui/icons-material/Extension';
import SyncIcon from '@mui/icons-material/Sync';
import CableIcon from '@mui/icons-material/Cable';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { integrationAPI } from '../../services/api';
import { IntegrationConfigFields } from '../../components/integrations/IntegrationConfigFields';
import { INTEGRATION_CATEGORIES, TenantIntegrationItem } from '../../constants/integrationRegistry';
import { catchyPrimaryButtonSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';

export const TenantIntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<TenantIntegrationItem[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    integrationAPI.list()
      .then((r) => setIntegrations(r.data))
      .catch(() => setError('Failed to load integrations'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const update = (provider: string, patch: Partial<TenantIntegrationItem>) => {
    setIntegrations((prev) => prev.map((i) => (i.provider === provider ? { ...i, ...patch } : i)));
  };

  const save = async (provider: string, item: TenantIntegrationItem) => {
    try {
      await integrationAPI.update(provider, { enabled: item.enabled, config: item.config || {} });
      setMessage(`${item.label} saved`);
      setError('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    }
  };

  const testConnection = async (provider: string, label: string) => {
    try {
      const r = await integrationAPI.testConnection(provider);
      setMessage(r.data.message || `${label} connection OK`);
    } catch {
      setError(`Connection test failed for ${label}`);
    }
  };

  const syncShopify = async () => {
    try {
      const r = await integrationAPI.syncShopify();
      setMessage(r.data.message);
    } catch {
      setError('Sync failed');
    }
  };

  const filtered = useMemo(() => (
    categoryFilter === 'all'
      ? integrations
      : integrations.filter((i) => i.category === categoryFilter)
  ), [integrations, categoryFilter]);

  const grouped = useMemo(() => {
    const featured = filtered.filter((i) => i.featured);
    const rest = filtered.filter((i) => !i.featured);
    return { featured, rest };
  }, [filtered]);

  const renderCard = (item: TenantIntegrationItem) => (
    <Grid item xs={12} md={6} key={item.provider}>
      <Paper sx={{
        p: 2.5, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`, height: '100%',
        ...(item.featured ? { borderColor: brandCss.primary, boxShadow: '0 4px 16px rgba(34,197,94,0.12)' } : {}),
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography fontWeight={700} fontSize={14}>{item.label}</Typography>
            <Typography fontSize={11} color="textSecondary">
              {item.categoryLabel || INTEGRATION_CATEGORIES[item.category]}
            </Typography>
          </Box>
          <Chip label={item.enabled ? 'Enabled' : 'Off'} size="small"
            sx={{
              bgcolor: item.enabled ? brandCss.green100 : '#f1f5f9',
              color: item.enabled ? brandCss.primaryDark : brandColors.textMuted,
              fontWeight: 700, fontSize: 11,
            }} />
        </Box>
        <Typography fontSize={12} color="textSecondary" mb={2}>{item.description}</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!item.enabled}
              onChange={(e) => update(item.provider, { enabled: e.target.checked })}
              color="success"
            />
          }
          label={<Typography fontSize={13}>Enable integration</Typography>}
        />
        <IntegrationConfigFields
          fields={item.fields || []}
          config={item.config || {}}
          onChange={(config) => update(item.provider, { config })}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" onClick={() => save(item.provider, item)} sx={catchyPrimaryButtonSx}>
            Save
          </Button>
          <Button variant="outlined" size="small" startIcon={<CableIcon />} onClick={() => testConnection(item.provider, item.label)}>
            Test
          </Button>
          {item.supportsSync && item.enabled && (
            <Button variant="outlined" size="small" startIcon={<SyncIcon />} onClick={syncShopify}>
              Sync Now
            </Button>
          )}
        </Box>
        {item.lastSyncAt && (
          <Typography fontSize={11} color="textSecondary" sx={{ mt: 1 }}>
            Last sync: {new Date(item.lastSyncAt).toLocaleString()} ({item.lastSyncStatus})
          </Typography>
        )}
      </Paper>
    </Grid>
  );

  return (
    <Box>
      <PageHeader
        title="Integrations"
        subtitle={getPageSubtitle('integrations')}
        icon={<ExtensionIcon />}

      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {!loading && integrations.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No integrations are available yet. Ask your platform administrator to enable integrations in Super Admin → Integrations.
        </Alert>
      )}

      <Box sx={{ mb: 2, maxWidth: 280 }}>
        <TextField
          select fullWidth size="small" label="Category"
          value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <MenuItem value="all">All Categories</MenuItem>
          {Object.entries(INTEGRATION_CATEGORIES).map(([key, label]) => (
            <MenuItem key={key} value={key}>{label}</MenuItem>
          ))}
        </TextField>
      </Box>

      {grouped.featured.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: brandCss.primaryDark }}>
            Featured
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {grouped.featured.map(renderCard)}
          </Grid>
        </>
      )}

      {grouped.rest.length > 0 && (
        <>
          {grouped.featured.length > 0 && (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>All Integrations</Typography>
          )}
          <Grid container spacing={2}>
            {grouped.rest.map(renderCard)}
          </Grid>
        </>
      )}
    </Box>
  );
};
